import { i18n } from '@/shared/i18n';

import { TEMPLATE_LOCK_ATTR, USER_LOCK_ATTR } from './sectionPicker.constants';
import { hasMarkupChildren } from './sectionPickerDom';

const DATA_UPDATABLE_REGEX = /data-updatable-([a-z0-9-]+)/g;

/**
 * The slug half of `data-updatable-tagged`, the marker the preview runtime stamps on every tagged
 * element so its CSS can outline them. It shares the `data-updatable-` prefix, so it matches the
 * regex above — but it is our own bookkeeping, never a section a user named.
 */
// eslint-disable-next-line i18next/no-literal-string -- attribute slug, not copy
export const RUNTIME_MARKER_SLUG = 'tagged';

export { TEMPLATE_LOCK_ATTR, USER_LOCK_ATTR };

export interface ElementLockState {
  /** Whether the agent is blocked from editing this element, by either marker, here or above it. */
  locked: boolean;
  /** Whether unticking the lock in the widget would actually free it — a user lock on THIS element. */
  canUnlock: boolean;
  /** Whether the lock comes from the template rather than the user. */
  lockedByTemplate: boolean;
}

/**
 * Read how the element matching `selector` is locked. Both markers protect descendants too, so a lock
 * inherited from an ancestor still reports `locked` — but not `canUnlock`, because removing the
 * attribute from the clicked element would be a no-op and leave the checkbox lying to the user.
 */
export function readLockState(rawHtml: string, selector: string): ElementLockState {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- querySelector on Element is valid
  const element = doc.documentElement.querySelector(selector);
  if (!element) return { locked: false, canUnlock: false, lockedByTemplate: false };

  // The element's OWN markers come from the DOM, which is authoritative about an element it found.
  // Its ANCESTORS do not: the parser relocates content out of a malformed table (foster parenting),
  // and a relocated element's DOM ancestors no longer include the locked container the source nests
  // it inside — so `closest()` called it unlocked and an edit went straight through a
  // `do-not-modify`. `null` means the source could not be walked, and the DOM is then the only
  // answer available.
  const ancestors = lockedAncestorsOf(rawHtml, selector);
  const closestAbove = (attribute: string) =>
    (element.parentElement?.closest(`[${attribute}]`) ?? null) !== null;
  const templateAbove = ancestors ? ancestors.template : closestAbove(TEMPLATE_LOCK_ATTR);
  const userAbove = ancestors ? ancestors.user : closestAbove(USER_LOCK_ATTR);

  const lockedByTemplate = element.hasAttribute(TEMPLATE_LOCK_ATTR) || templateAbove;
  const lockedByUser = element.hasAttribute(USER_LOCK_ATTR) || userAbove;
  return {
    locked: lockedByTemplate || lockedByUser,
    canUnlock: !lockedByTemplate && element.hasAttribute(USER_LOCK_ATTR),
    lockedByTemplate,
  };
}

/**
 * Which lock markers are held by the SOURCE ancestors of `selector`, or `null` when the element
 * cannot be located in the source.
 *
 * Walks a tag stack rather than asking the DOM, because the two disagree exactly where it matters:
 * the parser moves foster-parented content out from under the container that locks it.
 */
function lockedAncestorsOf(
  rawHtml: string,
  selector: string,
): { template: boolean; user: boolean } | null {
  const resolved = resolveRawElementSpan(rawHtml, selector, { needsSpan: false });
  if (!resolved.ok) return null;

  const maskedRanges = getMaskedRanges(rawHtml);
  const tagRegex = /<(\/?)([a-zA-Z][^\s/>]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;
  const stack: Array<{ name: string; template: boolean; user: boolean }> = [];

  let match: RegExpExecArray | null;
  while ((match = tagRegex.exec(rawHtml)) !== null && match.index < resolved.span.openStart) {
    if (isInsideMaskedRange(match.index, maskedRanges)) continue;
    const name = match[2].toLowerCase();

    if (match[1]) {
      // Truncate rather than pop: everything above the match had an implied close, which is legal.
      const opened = stack.map((entry) => entry.name).lastIndexOf(name);
      if (opened !== -1) stack.length = opened;
      continue;
    }
    if (VOID_ELEMENTS.has(name) || match[3].trimEnd().endsWith('/')) continue;

    const attrs = parseRawAttributes(match[3]);
    stack.push({
      name,
      template: attrs.has(TEMPLATE_LOCK_ATTR),
      user: attrs.has(USER_LOCK_ATTR),
    });
  }

  return {
    template: stack.some((entry) => entry.template),
    user: stack.some((entry) => entry.user),
  };
}

/** What deleting an element would take with it. `null` when the source span cannot be resolved. */
export interface DeletionImpact {
  /** Section slugs that would disappear — the element's own included. */
  sections: string[];
  /** Whether anything strictly INSIDE the element carries a lock marker. */
  hasLockedDescendant: boolean;
}

/**
 * Both questions a delete confirmation has to answer, from ONE resolve of the span that will be cut.
 *
 * They used to be asked separately, and of different things: the section list scanned the raw span
 * while the lock check walked the parsed subtree. Those are different regions wherever the source
 * nests wider than the tree, so the dialog could report nothing at risk and then delete protected
 * copy. `null` means "cannot tell" — never "nothing to lose".
 */
export function describeDeletionImpact(rawHtml: string, selector: string): DeletionImpact | null {
  const resolved = resolveRawElementSpan(rawHtml, selector, { needsSpan: true });
  if (!resolved.ok) return null;
  const { span } = resolved;

  return {
    // The element's own opening tag counts here — deleting it takes its own slug too.
    sections: extractUpdatableSections(rawHtml.slice(span.openStart, span.closeEnd)),
    // ...and must NOT count here, where the question is only about descendants. A `<p do-not-modify>`
    // is not blocked from being deleted by its own marker.
    hasLockedDescendant: regionHasLockMarker(rawHtml, span.contentStart, span.closeStart),
  };
}

/**
 * Does a real opening tag between `start` and `end` carry a lock marker?
 *
 * Searching the slice for the token would be wrong twice over: `getMaskedRanges` does not mask TEXT
 * nodes, so body copy reading "please do-not-modify this" would register, and a marker is a legal
 * substring of a longer name (`data-locked-by`). Walking opening tags and parsing their attribute
 * regions gets both right, and reuses the mask for comments, raw-text bodies and attribute values.
 */
function regionHasLockMarker(rawHtml: string, start: number, end: number): boolean {
  const maskedRanges = getMaskedRanges(rawHtml);
  const tagRegex = /<[a-zA-Z][^\s/>]*((?:"[^"]*"|'[^']*'|[^>"'])*)\/?>/g;
  tagRegex.lastIndex = start;

  let tag: RegExpExecArray | null;
  while ((tag = tagRegex.exec(rawHtml)) !== null && tag.index < end) {
    if (isInsideMaskedRange(tag.index, maskedRanges)) continue;
    const attrs = parseRawAttributes(tag[1]);
    if (attrs.has(TEMPLATE_LOCK_ATTR) || attrs.has(USER_LOCK_ATTR)) return true;
  }
  return false;
}

/**
 * Whether anything INSIDE `selector` carries a lock marker — what deleting it would destroy.
 *
 * Separate from `readLockState` on purpose. That function answers "is this element under a lock",
 * which is what the widget's lock checkbox needs; making a container report `locked` because
 * something beneath it is locked would break that control. Deleting asks the opposite question, and
 * a container is exactly what a template author clicks when they mean to remove a whole block.
 *
 * A bare boolean cannot distinguish "no lock" from "could not tell", so callers that act on the
 * answer should prefer {@link describeDeletionImpact}.
 */
export function containsLockedDescendant(rawHtml: string, selector: string): boolean {
  return describeDeletionImpact(rawHtml, selector)?.hasLockedDescendant ?? false;
}

/**
 * Elements whose contents are parsed as plain text rather than markup. A `<td>`-like string
 * inside their body (e.g. CSS in `<style>`, JS in `<script>`, or text in `<title>`/`<textarea>`)
 * is NOT a real element — the DOM never creates a node for it.
 */
/* eslint-disable i18next/no-literal-string -- HTML tag names and regex sources */
const RAWTEXT_ELEMENTS = ['script', 'style', 'textarea', 'title'];

/**
 * Byte ranges `[start, end)` of spans where `<tag>`-like text is NOT real markup: HTML comments
 * (including Outlook MSO conditionals `<!--[if mso]> … <![endif]-->`) and the bodies of raw-text /
 * escapable-raw-text elements (`script`, `style`, `textarea`, `title`).
 *
 * Why this exists: the mutation helpers below locate the clicked element's ordinal via
 * `querySelectorAll(tagName)` (which only ever sees real elements) and then map that ordinal back
 * to the source by counting opening-tag matches in the raw STRING. A naive string scan also counts
 * tag-like text inside comments / raw-text bodies, so the string ordinal drifts from the DOM
 * ordinal and the wrong (often adjacent) element gets mutated. Skipping matches that start inside
 * these ranges realigns the two.
 *
 * The result is sorted and disjoint — see {@link isInsideMaskedRange}, which relies on it.
 */
export function getMaskedRanges(html: string): Array<[number, number]> {
  // ORDER MATTERS, and the stages are why. Raw-text bodies are established first, quoted attribute
  // values are gathered from tags that do not start inside one, and only then are comments scanned
  // against that union. Scanning comments first meant a `<!--` that is not a comment — legal inside
  // a `<style>` body (RAWTEXT) or inside `alt="… <!-- …"` — opened a mask that ran to the next
  // `-->` ANYWHERE in the document, swallowing real markup along the way. Email HTML is full of
  // `<![endif]-->`, so a downstream `-->` is easy to come by.
  //
  // Each stage merges, so the membership test below can binary-search instead of scanning a list
  // that grows with every attribute value in the document.
  const rawTextBodies: Array<[number, number]> = [];
  for (const tag of RAWTEXT_ELEMENTS) {
    // Capture the opening tag and body separately so the masked span covers only the body, never
    // the surrounding markup. The opening tag is matched quote-aware (same token as
    // `buildOpenTagRegex`) so a `>` inside one of its attribute values doesn't truncate it.
    const bodyRegex = new RegExp(
      `(<${tag}\\b(?:"[^"]*"|'[^']*'|[^>"'])*>)([\\s\\S]*?)(</${tag}\\s*>)`,
      'gi',
    );
    let body: RegExpExecArray | null;
    while ((body = bodyRegex.exec(html)) !== null) {
      const bodyStart = body.index + body[1].length;
      rawTextBodies.push([bodyStart, bodyStart + body[2].length]);
    }
  }
  const throughRawText = mergeRanges(rawTextBodies);

  // Quoted attribute values. `<td>`-like text inside one is not markup either, so masking them also
  // stops the open-tag walk counting a tag that only exists inside someone's `alt` text.
  //
  // Gating on the raw-text ranges alone, rather than on everything gathered so far, is equivalent:
  // `exec` advances `lastIndex` monotonically, so no tag can start inside an earlier tag's own
  // attribute range.
  const attributeValues: Array<[number, number]> = [];
  const tagRegex = /<[a-zA-Z][^\s/>]*((?:"[^"]*"|'[^']*'|[^>"'])*)\/?>/g;
  const quotedRegex = /"[^"]*"|'[^']*'/g;
  let tag: RegExpExecArray | null;
  while ((tag = tagRegex.exec(html)) !== null) {
    if (isInsideMaskedRange(tag.index, throughRawText)) continue;
    const attrsStart = tag.index + tag[0].indexOf(tag[1], 1);
    quotedRegex.lastIndex = 0;
    let quoted: RegExpExecArray | null;
    while ((quoted = quotedRegex.exec(tag[1])) !== null) {
      // +1/-1 trims the quote characters themselves, so the mask covers only the value.
      const start = attrsStart + quoted.index + 1;
      attributeValues.push([start, start + quoted[0].length - 2]);
    }
  }
  const throughAttributes = mergeRanges([...throughRawText, ...attributeValues]);

  const comments: Array<[number, number]> = [];
  const commentRegex = /<!--[\s\S]*?-->/g;
  let comment: RegExpExecArray | null;
  while ((comment = commentRegex.exec(html)) !== null) {
    if (isInsideMaskedRange(comment.index, throughAttributes)) continue;
    comments.push([comment.index, comment.index + comment[0].length]);
  }

  return mergeRanges([...throughAttributes, ...comments]);
}

/** Sort and coalesce into the disjoint form {@link isInsideMaskedRange} requires. */
function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  if (ranges.length < 2) return ranges.map(([start, end]): [number, number] => [start, end]);
  const sorted = [...ranges].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: Array<[number, number]> = [[sorted[0][0], sorted[0][1]]];
  for (let index = 1; index < sorted.length; index++) {
    const last = merged[merged.length - 1];
    const [start, end] = sorted[index];
    // Touching counts as overlapping: `[0,5)` and `[5,10)` describe exactly the membership `[0,10)`
    // does, so coalescing them keeps the list minimal without changing any answer.
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}

/**
 * Is `offset` inside one of `ranges`?
 *
 * PRECONDITION: `ranges` is sorted by start and disjoint — i.e. it came from {@link getMaskedRanges}.
 * This binary-searches, so an unsorted array yields a silent `false` rather than an error. That
 * unwritten contract is why this is not part of the slice's public API.
 */
export function isInsideMaskedRange(offset: number, ranges: Array<[number, number]>): boolean {
  let low = 0;
  let high = ranges.length - 1;
  while (low <= high) {
    const mid = (low + high) >>> 1;
    const [start, end] = ranges[mid];
    if (offset < start) high = mid - 1;
    else if (offset >= end) low = mid + 1;
    else return true;
  }
  return false;
}

/**
 * Build a quote-aware regex matching an opening `<tagName …>` tag, captured as
 * `(open)(attrs)(close)`. The attribute group swallows whole quoted strings so a `>` inside a
 * quoted attribute value (e.g. `<td title="a > b">`) does not prematurely terminate the tag —
 * which would both miscount tags and corrupt the output.
 */
export function buildOpenTagRegex(tagName: string): RegExp {
  return new RegExp(`(<${tagName}\\b)((?:"[^"]*"|'[^']*'|[^>"'])*?)(\\s*/?>)`, 'gi');
}

/**
 * Build a regex matching a CLOSING `</tagName>` tag, tolerating spec-legal whitespace before `>`
 * (`</div >`, `</div\n>`). The open matcher is already whitespace-tolerant; matching the close exactly
 * (`indexOf('</div>')`) would miss these forms and let the close-tag walk splice into an ancestor,
 * corrupting the document. Global + case-insensitive to mirror `buildOpenTagRegex`.
 */
export function buildCloseTagRegex(tagName: string): RegExp {
  return new RegExp(`</${tagName}\\s*>`, 'gi');
}

/**
 * Build a regex that matches the EXACT `data-updatable-{sectionName}` attribute, prefix-safe.
 *
 * The trailing `(?![A-Za-z0-9-])` lookahead ensures the name is not merely a prefix of a longer
 * slug — without it, matching `data-updatable-hero` would also match inside `data-updatable-hero-2`,
 * which corrupts sibling tags on remove and falsely reports "already exists" on add. Section names
 * are slugs (`[a-z0-9-]`), so they contain no regex metacharacters and are safe to interpolate.
 */
function buildUpdatableAttrRegex(sectionName: string, flags = ''): RegExp {
  return new RegExp(`data-updatable-${sectionName}(?![A-Za-z0-9-])`, flags);
}
/**
 * Does `sectionName`'s attribute appear as REAL markup — not inside a comment, a raw-text body, or a
 * quoted attribute value?
 *
 * The token is an attribute NAME, and an attribute name never sits inside a quoted value. So masking
 * those three contexts is exactly the difference between the attribute and a mention of it: a
 * `data-updatable-hero` written in someone's `alt` text, or left in a comment, is copy.
 */
function hasUpdatableAttr(rawHtml: string, sectionName: string): boolean {
  const ranges = getMaskedRanges(rawHtml);
  const regex = buildUpdatableAttrRegex(sectionName, 'g');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(rawHtml)) !== null) {
    if (!isInsideMaskedRange(match.index, ranges)) return true;
  }
  return false;
}
/* eslint-enable i18next/no-literal-string */

/** Byte offsets of an element in the SOURCE string. `closeStart === closeEnd === contentStart` for a void element. */
interface RawElementSpan {
  openStart: number;
  contentStart: number;
  closeStart: number;
  closeEnd: number;
  tagName: string;
}

/** Attribute name → raw (still entity-encoded) value, parsed out of an opening tag's attribute region. */
function parseRawAttributes(attrsRegion: string): Map<string, string> {
  const attrs = new Map<string, string>();
  const attrRegex = /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?/g;
  let match: RegExpExecArray | null;
  while ((match = attrRegex.exec(attrsRegion)) !== null) {
    if (!match[1]) continue;
    // Double-quoted, single-quoted, then bare — whichever alternative actually matched. The cast
    // states what the DOM lib type gets wrong: an optional group that did not participate is
    // `undefined` at runtime, though `RegExpExecArray` is typed as `string[]`.
    const groups = match.slice(2, 5) as Array<string | undefined>;
    const name = match[1].toLowerCase();
    // FIRST occurrence wins, matching the HTML parser: "the parser ignores all such duplicate
    // occurrences of the attribute" (WHATWG 13.2.5.33). `Map.set` would keep the LAST, so on
    // `<td width="1" width="2">` this map disagreed with the DOM about a value the DOM reports as
    // `1` — and every comparison against a parsed element failed.
    if (!attrs.has(name)) attrs.set(name, groups.find((group) => group !== undefined) ?? '');
  }
  return attrs;
}

/**
 * Transient attribute stamped onto raw opening tags so the HTML parser itself performs the
 * source↔DOM mapping. Minted for a single resolve and never written back to the template — the same
 * idea as `EDIT_REF_ATTR` in the backend's `edit-instrumentation.ts`.
 */
const RAW_INDEX_ATTR = 'data-sp-raw-index';

/** Byte range `[start, end)` of one opening tag in the ORIGINAL source string. */
interface StampedTag {
  start: number;
  end: number;
}

/** A stamp name the document does not already contain, so instrumenting cannot shadow real markup. */
function uniqueStampName(rawHtml: string): string {
  let name = RAW_INDEX_ATTR;
  let suffix = 0;
  while (rawHtml.includes(name)) name = `${RAW_INDEX_ATTR}-${(suffix += 1)}`;
  return name;
}

/**
 * A copy of `rawHtml` in which every unmasked `<tagName …>` opening tag carries its index in the
 * returned `tags` array. Parsing that copy and reading the stamp off a matched element maps it back
 * to its exact source offsets, with no ordinal arithmetic in between.
 *
 * Only the target's tag name is stamped: it is all a resolve needs, and it keeps the rewritten
 * string close to the original.
 */
function instrumentOpenTags(
  rawHtml: string,
  tagName: string,
  maskedRanges: Array<[number, number]>,
  stampName: string,
): { instrumented: string; tags: StampedTag[] } {
  const tags: StampedTag[] = [];
  // Assembled by join rather than by appending to a string: the latter recopies the whole document
  // once per tag, which is quadratic on the templates this runs against.
  const pieces: string[] = [];
  let copiedTo = 0;

  const openTagRegex = buildOpenTagRegex(tagName);
  let open: RegExpExecArray | null;
  while ((open = openTagRegex.exec(rawHtml)) !== null) {
    if (isInsideMaskedRange(open.index, maskedRanges)) continue;
    // Immediately after `<tagName`, which is where an attribute is always legal.
    const insertAt = open.index + open[1].length;
    pieces.push(rawHtml.slice(copiedTo, insertAt), ` ${stampName}="${tags.length}"`);
    copiedTo = insertAt;
    tags.push({ start: open.index, end: open.index + open[0].length });
  }
  pieces.push(rawHtml.slice(copiedTo));

  return { instrumented: pieces.join(''), tags };
}

/**
 * Does the span close every tag it opens, and open every tag it closes?
 *
 * Catches a splice that swallowed a foreign closing tag — `<b><span>x</b></span>`, where walking
 * from `<span>` to the first `</…>` consumes the `</b>` and leaves `<b>` unclosed in the document.
 */
function spanIsBalanced(
  rawHtml: string,
  contentStart: number,
  contentEnd: number,
  maskedRanges: Array<[number, number]>,
): boolean {
  const content = rawHtml.slice(contentStart, contentEnd);
  const tagRegex = /<(\/?)([a-zA-Z][^\s/>]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;
  const stack: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = tagRegex.exec(content)) !== null) {
    // A tag inside a comment or a raw-text body is text, not markup — an MSO conditional routinely
    // carries a commented-out `</td>` that would otherwise read as a foreign close.
    if (isInsideMaskedRange(contentStart + match.index, maskedRanges)) continue;
    const name = match[2].toLowerCase();
    if (match[1]) {
      const open = stack.lastIndexOf(name);
      if (open === -1) return false;
      // Truncate rather than pop: everything above it had an implied close, which is legal.
      stack.length = open;
    } else if (!VOID_ELEMENTS.has(name) && !match[3].trimEnd().endsWith('/')) {
      // The trailing slash has to be read off the attribute region, not a group of its own: the
      // attribute alternative `[^>"']` matches `/` too, so for `<path/>` it is group 3 that consumes
      // the slash and a dedicated `(\/?)` group can never capture anything.
      stack.push(name);
    }
  }
  return true;
}

/**
 * Locate `selector` in the SOURCE string, or return null when that cannot be done with confidence.
 *
 * These helpers mutate raw HTML rather than re-serialising a DOM, because re-serialising destroys
 * Outlook conditional comments and normalises markup. The cost is that the element has to be found
 * twice — once in the DOM, once in the string — and the two countings diverge wherever the parser
 * synthesises a tag (`<table><td>` gains a `<tr>`) or relocates one (foster parenting). When they
 * diverge the string offset points at a DIFFERENT element, and the caller edits something the user
 * never named.
 *
 * So every result is verified before it is handed back, and an unverifiable result is refused.
 * Failing closed is the whole point: a caller that reports "could not locate the element" costs the
 * user a retry, where the old silent drift cost them content they did not know they had lost.
 *
 * `needsSpan` is false for the attribute mutators, which only rewrite the opening tag — requiring a
 * matching close would make them refuse elements they handle correctly today.
 */
type RawSpanResult =
  | { ok: true; span: RawElementSpan }
  /** `no-match`: the selector hit nothing. `unverifiable`: it hit an element we cannot safely locate in the source. */
  | { ok: false; reason: 'no-match' | 'unverifiable' }
  | { ok: false; reason: 'no-close'; tagName: string };

function resolveRawElementSpan(
  rawHtml: string,
  selector: string,
  { needsSpan }: { needsSpan: boolean },
): RawSpanResult {
  const parser = new DOMParser();
  /* eslint-disable @typescript-eslint/no-deprecated -- standard DOM API */
  const doc = parser.parseFromString(rawHtml, 'text/html');
  const element = doc.documentElement.querySelector(selector);
  if (!element) return { ok: false, reason: 'no-match' };

  const tagName = element.tagName.toLowerCase();
  const maskedRanges = getMaskedRanges(rawHtml);

  // Stamp the source's opening tags and let the PARSER say which element each one became. The
  // alternative — take the element's ordinal among `querySelectorAll(tagName)`, then count that many
  // opening tags in the string — holds only while both orderings agree, and they diverge wherever
  // the parser synthesises a tag (`<table><td>` gains a `<tr>`), drops one, relocates one (foster
  // parenting) or clones one (the adoption agency). All four are ordinary in email HTML, and the
  // drift wrote the user's edit onto an element they never selected.
  const stampName = uniqueStampName(rawHtml);
  const { instrumented, tags } = instrumentOpenTags(rawHtml, tagName, maskedRanges, stampName);
  const stampedDoc = parser.parseFromString(instrumented, 'text/html');
  const target = stampedDoc.documentElement.querySelector(selector);
  if (!target) return { ok: false, reason: 'unverifiable' };

  // No stamp means the parser invented this element, so no opening tag in the source corresponds to
  // it. Two elements wearing one stamp means it cloned a source tag, and rewriting that single
  // offset would change both.
  const stamp = target.getAttribute(stampName);
  if (stamp === null) return { ok: false, reason: 'unverifiable' };
  if (stampedDoc.documentElement.querySelectorAll(`[${stampName}="${stamp}"]`).length > 1) {
    return { ok: false, reason: 'unverifiable' };
  }
  /* eslint-enable @typescript-eslint/no-deprecated */

  // The stamp is our own and should always be in range; refusing rather than trusting it keeps a
  // malformed value from indexing off the end and producing nonsense offsets.
  const stampIndex = Number(stamp);
  if (!Number.isInteger(stampIndex) || stampIndex < 0 || stampIndex >= tags.length) {
    return { ok: false, reason: 'unverifiable' };
  }

  const { start: openStart, end: contentStart } = tags[stampIndex];
  if (VOID_ELEMENTS.has(tagName) || !needsSpan) {
    return {
      ok: true,
      span: { openStart, contentStart, closeStart: contentStart, closeEnd: contentStart, tagName },
    };
  }

  const closeRegex = buildCloseTagRegex(tagName);
  const nestedOpenRegex = buildOpenTagRegex(tagName);
  const findRealClose = (from: number): { index: number; length: number } | null => {
    closeRegex.lastIndex = from;
    let m: RegExpExecArray | null;
    while ((m = closeRegex.exec(rawHtml)) !== null) {
      if (!isInsideMaskedRange(m.index, maskedRanges)) {
        return { index: m.index, length: m[0].length };
      }
    }
    return null;
  };

  let depth = 1;
  let pos = contentStart;
  while (depth > 0 && pos < rawHtml.length) {
    const close = findRealClose(pos);
    if (close === null) return { ok: false, reason: 'no-close', tagName };

    nestedOpenRegex.lastIndex = pos;
    let nextOpen: RegExpExecArray | null = null;
    while ((nextOpen = nestedOpenRegex.exec(rawHtml)) !== null && nextOpen.index < close.index) {
      if (!isInsideMaskedRange(nextOpen.index, maskedRanges)) depth++;
    }

    depth--;
    if (depth === 0) {
      if (!spanIsBalanced(rawHtml, contentStart, close.index, maskedRanges)) {
        return { ok: false, reason: 'unverifiable' };
      }
      // The source span must hold the same text the DOM element does. A span that swallowed a
      // neighbour's content (crossed tags) carries text the element never had.
      const content = rawHtml.slice(contentStart, close.index);
      const probe = parser.parseFromString(`<body>${content}</body>`, 'text/html');
      if ((probe.body.textContent ?? '') !== (element.textContent ?? ''))
        return { ok: false, reason: 'unverifiable' };

      return {
        ok: true,
        span: {
          openStart,
          contentStart,
          closeStart: close.index,
          closeEnd: close.index + close.length,
          tagName,
        },
      };
    }
    pos = close.index + close.length;
  }
  return { ok: false, reason: 'unverifiable' };
}

/** The caller-facing message for a resolve failure — "nothing matched" reads very differently from
 *  "matched, but the source could not be located safely". */
function resolveErrorMessage(
  failure: Extract<RawSpanResult, { ok: false }>,
  selector: string,
): string {
  if (failure.reason === 'no-match') {
    return i18n.t('common:sectionPicker.sectionPickerUtils.noElementMatches', { selector });
  }
  if (failure.reason === 'no-close') {
    return i18n.t('common:sectionPicker.sectionPickerUtils.closingTagNotFound', {
      tagName: failure.tagName,
    });
  }
  return i18n.t('common:sectionPicker.sectionPickerUtils.couldNotLocateElement');
}

/** Split a resolved opening tag back into the `(open)(attrs)(close)` parts the rewriters need. */
function splitOpenTag(
  openTag: string,
  tagName: string,
): { open: string; attrs: string; close: string } | null {
  const match = buildOpenTagRegex(tagName).exec(openTag);
  if (!match) return null;
  const [, open, attrs, close] = match;

  // The close group `\s*/?>` claims any trailing `/` as a self-closing marker, but inside an
  // UNQUOTED attribute value the slash is part of the value — `href=http://x.com/` is one token to
  // the parser, and rewriting the tag with the slash treated as a marker silently truncated the URL.
  // Only a `/` that directly follows an unquoted value belongs to that value; one preceded by
  // whitespace, a closing quote, a bare attribute name or the tag name really is a marker.
  const lastToken = attrs.split(/\s/).pop() ?? '';
  const endsInUnquotedValue = lastToken.includes('=') && !/["']$/.test(lastToken);
  if (close.startsWith('/') && endsInUnquotedValue) {
    return { open, attrs: `${attrs}/`, close: close.slice(1) };
  }

  return { open, attrs, close };
}

/**
 * Convert user input to a valid slug for use in data-updatable-{slug}.
 * "My Section Name" → "my-section-name"
 */
export function slugify(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Extract all data-updatable-* section names from raw HTML.
 * Returns the slug part (e.g., "hero-headline" from "data-updatable-hero-headline").
 */
export function extractUpdatableSections(rawHtml: string): string[] {
  const sections: string[] = [];
  let match: RegExpExecArray | null;
  // eslint-disable-next-line i18next/no-literal-string -- regex flag
  const regex = new RegExp(DATA_UPDATABLE_REGEX.source, 'g');
  while ((match = regex.exec(rawHtml)) !== null) {
    if (match[1] !== RUNTIME_MARKER_SLUG && !sections.includes(match[1])) {
      sections.push(match[1]);
    }
  }
  return sections;
}

/**
 * The section slugs carried by `selector` or anything inside it — what deleting that element would
 * take with it.
 *
 * Deliberately built on `extractUpdatableSections` rather than its own scan: the number shown in a
 * delete confirmation has to agree with the list the editor's contents panel renders, and the only
 * way to guarantee that is to share the parse. Returns `[]` when the selector matches nothing, so a
 * caller must treat a miss as "unknown", not as "nothing to lose".
 */
export function extractUpdatableSectionsWithin(rawHtml: string, selector: string): string[] | null {
  // Scanned over the span `removeElement` will actually cut, not over the parsed element's
  // `outerHTML`. Those are different strings wherever the source nests wider than the tree, and the
  // warning describing one while the delete performs the other is how a confirmation ends up
  // promising safety it never established. `null` means "cannot tell" — never "nothing to lose".
  const resolved = resolveRawElementSpan(rawHtml, selector, { needsSpan: true });
  if (!resolved.ok) return null;
  return extractUpdatableSections(rawHtml.slice(resolved.span.openStart, resolved.span.closeEnd));
}

/**
 * Add a data-updatable-{sectionName} attribute to the element matching
 * the given CSS selector in the raw HTML string.
 *
 * Uses DOMParser to locate the element, then performs a targeted string
 * replacement on the raw HTML to preserve original formatting.
 */
export function addUpdatableAttribute(
  rawHtml: string,
  selector: string,
  sectionName: string,
): { html: string; success: boolean; error?: string } {
  const attrName = `data-updatable-${sectionName}`;

  // Prefix-safe AND context-aware: a plain `includes(attrName)` would treat `data-updatable-hero` as
  // present when only `data-updatable-hero-2` exists, and a raw scan would treat a mention of the
  // token in an `alt` or a comment as the attribute itself. Both falsely reject the add.
  if (hasUpdatableAttr(rawHtml, sectionName)) {
    return {
      html: rawHtml,
      success: false,
      error: i18n.t('common:sectionPicker.sectionPickerUtils.alreadyExistsInThe', {
        attributeName: attrName,
      }),
    };
  }

  const resolved = resolveRawElementSpan(rawHtml, selector, { needsSpan: false });
  if (!resolved.ok) {
    return { html: rawHtml, success: false, error: resolveErrorMessage(resolved, selector) };
  }
  const { span } = resolved;

  const parts = splitOpenTag(rawHtml.slice(span.openStart, span.contentStart), span.tagName);
  if (!parts) {
    return {
      html: rawHtml,
      success: false,
      error: i18n.t('common:sectionPicker.sectionPickerUtils.couldNotLocateElement'),
    };
  }

  const rewritten = `${parts.open}${parts.attrs} ${attrName}${parts.close}`;
  return {
    html: rawHtml.slice(0, span.openStart) + rewritten + rawHtml.slice(span.contentStart),
    success: true,
  };
}

/* eslint-disable i18next/no-literal-string -- HTML void element tag names */
const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
]);
/* eslint-enable i18next/no-literal-string */

/**
 * Whether `tagName` is a void element — one that cannot hold content, so any attempt to replace
 * its body is rejected by {@link replaceElementContent}. Callers use this to decide whether to
 * offer a content editor at all, rather than discovering the rejection on save.
 */
export function isVoidElement(tagName: string): boolean {
  return VOID_ELEMENTS.has(tagName.toLowerCase());
}

/**
 * Replace the content (innerHTML) of the element matching the given CSS selector.
 * Uses DOMParser for element lookup and regex-based string replacement to preserve formatting.
 */
export function replaceElementContent(
  rawHtml: string,
  selector: string,
  newContent: string,
): { html: string; success: boolean; error?: string } {
  const resolved = resolveRawElementSpan(rawHtml, selector, { needsSpan: true });
  if (!resolved.ok) {
    return { html: rawHtml, success: false, error: resolveErrorMessage(resolved, selector) };
  }
  const { span } = resolved;

  // A void element has no content to replace; writing "between" its tags would corrupt the document.
  if (VOID_ELEMENTS.has(span.tagName)) {
    return {
      html: rawHtml,
      success: false,
      error: i18n.t('common:sectionPicker.sectionPickerUtils.elementsHaveNoEditable', {
        tagName: span.tagName,
      }),
    };
  }

  return {
    html: rawHtml.slice(0, span.contentStart) + newContent + rawHtml.slice(span.closeStart),
    success: true,
  };
}

/**
 * Remove the whole element matching `selector` (opening tag, contents, and closing tag) from the raw
 * HTML — the user-only "Remove element" action. Uses the same selector→ordinal→masked-range→nesting
 * mechanics as {@link replaceElementContent}, but splices out the entire element span rather than
 * replacing its inner content. Void elements (`<img>`, `<br>`, …) have no closing tag, so only the
 * opening tag is removed. Returns the HTML unchanged with `success:false` when the element can't be
 * located.
 */
export function removeElement(
  rawHtml: string,
  selector: string,
): { html: string; success: boolean; error?: string } {
  const resolved = resolveRawElementSpan(rawHtml, selector, { needsSpan: true });
  if (!resolved.ok) {
    return { html: rawHtml, success: false, error: resolveErrorMessage(resolved, selector) };
  }
  const { span } = resolved;

  // `closeEnd === contentStart` for a void element, so this removes just the opening tag there.
  return {
    html: rawHtml.slice(0, span.openStart) + rawHtml.slice(span.closeEnd),
    success: true,
  };
}

/**
 * Remove a data-updatable-{sectionName} attribute from the raw HTML.
 *
 * Prefix-safe: removing `hero` must not corrupt `data-updatable-hero-2`. The name-end lookahead
 * sits before the optional `="…"` so both the boolean form (`data-updatable-hero`) and a valued
 * form are stripped, while a longer sibling slug is left untouched.
 */
export function removeUpdatableAttribute(rawHtml: string, sectionName: string): string {
  return rawHtml.replace(
    // eslint-disable-next-line i18next/no-literal-string -- HTML attribute name pattern
    new RegExp(`\\s*data-updatable-${sectionName}(?![A-Za-z0-9-])(?:="[^"]*")?`, 'g'),
    '',
  );
}

/**
 * Rename a `data-updatable-{oldName}` attribute to `data-updatable-{newName}` in place.
 *
 * A tagged element is uniquely identified by its `data-updatable-{slug}` (slugs are unique per
 * template), so renaming keys off the slug rather than a click-derived CSS selector. That selector
 * is `[data-updatable-{oldName}]` and would be destroyed by a remove-then-re-add flow before it
 * could be used; renaming in place sidesteps that entirely and is immune to positional drift. The
 * element's position, other attributes, and any `data-selection-helper` are preserved.
 */
export function renameUpdatableSection(
  rawHtml: string,
  oldName: string,
  newName: string,
): { html: string; success: boolean; error?: string } {
  if (oldName === newName) {
    return { html: rawHtml, success: true };
  }
  if (!hasUpdatableAttr(rawHtml, oldName)) {
    return {
      html: rawHtml,
      success: false,
      error: i18n.t('common:sectionPicker.sectionPickerUtils.noElementMatches', {
        selector: `[data-updatable-${oldName}]`,
      }),
    };
  }
  if (hasUpdatableAttr(rawHtml, newName)) {
    return {
      html: rawHtml,
      success: false,
      error: i18n.t('common:sectionPicker.sectionPickerUtils.alreadyExistsInThe', {
        attributeName: `data-updatable-${newName}`,
      }),
    };
  }
  // Zero-width name-end lookahead means only the attribute NAME is swapped; any value and the
  // following character are left in place. Masked offsets are skipped so a mention of the token in
  // copy is not rewritten along with the real attribute.
  const ranges = getMaskedRanges(rawHtml);
  // eslint-disable-next-line i18next/no-literal-string -- regex flag
  const html = rawHtml.replace(buildUpdatableAttrRegex(oldName, 'g'), (match, offset: number) =>
    isInsideMaskedRange(offset, ranges) ? match : `data-updatable-${newName}`,
  );
  return { html, success: true };
}

/**
 * Match one attribute and its value inside an opening tag, in every form the HTML spec allows.
 *
 * Three details are load-bearing, and getting any of them wrong corrupts the tag rather than failing
 * cleanly: the NAME is regex-escaped so a special character in it stays literal; `(?![\w:-])` is a
 * name boundary, so stripping `data-locked` does not match inside `data-locked-by` (or `src` inside
 * `srcset`); and the value group accepts double-quoted, single-quoted AND unquoted forms, so
 * `x='1'`/`x=1` are removed whole instead of leaving a dangling `='1'` behind. The value is optional
 * so the valueless form (`<p data-locked>`) matches too.
 *
 * Shared by every attribute read/write/remove helper below — they previously each carried their own
 * pattern, and the weaker ones ate neighbouring attribute values and left the target in place.
 */
// eslint-disable-next-line i18next/no-literal-string -- regex flag
function buildAttrRegex(attrName: string, flags = 'i'): RegExp {
  const escaped = attrName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\s*${escaped}(?![\\w:-])(?:\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s"'>]+))?`, flags);
}

/**
 * Remove `attrName` from an opening tag's ATTRIBUTE SPAN (the text between the tag name and `>`),
 * preserving every other byte.
 *
 * Deliberately a scan rather than a regex: no regex can tell an attribute NAME from the same text
 * appearing inside another attribute's VALUE, so `alt="see data-locked spec"` used to have
 * ` data-locked` cut out of the middle of its value while the real marker survived — corrupting one
 * attribute and silently failing to remove the target. Walking name/value pairs quote-aware is the
 * only way to know where a name actually starts. Matching is case-insensitive, because HTML
 * attribute names are.
 */
function removeAttrFromSpan(attrs: string, attrName: string): string {
  const target = attrName.toLowerCase();
  let out = '';
  let i = 0;
  while (i < attrs.length) {
    const wsStart = i;
    while (i < attrs.length && /\s/.test(attrs[i])) i++;
    const nameStart = i;
    while (i < attrs.length && !/[\s=]/.test(attrs[i])) i++;
    const name = attrs.slice(nameStart, i);
    if (!name) {
      // Trailing whitespace (or nothing left) — keep it and stop.
      out += attrs.slice(wsStart);
      break;
    }
    // Optional `= value`, where the value may be double-quoted, single-quoted, or bare.
    const afterName = i;
    while (i < attrs.length && /\s/.test(attrs[i])) i++;
    if (attrs[i] === '=') {
      i++;
      while (i < attrs.length && /\s/.test(attrs[i])) i++;
      const quote = attrs[i];
      if (quote === '"' || quote === "'") {
        i++;
        while (i < attrs.length && attrs[i] !== quote) i++;
        if (i < attrs.length) i++; // closing quote
      } else {
        while (i < attrs.length && !/[\s>]/.test(attrs[i])) i++;
      }
    } else {
      // No value — rewind past the whitespace we consumed looking for `=`.
      i = afterName;
    }
    if (name.toLowerCase() !== target) out += attrs.slice(wsStart, i);
  }
  return out;
}

/**
 * Escape a value so it is safe inside a double-quoted HTML attribute. Escaping `"` is what stops a
 * quote in the value from closing the attribute early and corrupting the tag. Ampersand must be
 * escaped first so we don't double-escape the entities we introduce.
 */
function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Escape a PLAIN-TEXT string so it can be spliced into markup as text rather than parsed as it.
 *
 * The counterpart to {@link escapeHtmlAttribute}, for element bodies instead of attribute values.
 * Anything read out of the DOM as `textContent` arrives already decoded, so writing it back into
 * the source unescaped promotes it to live markup — `a &lt;b&gt; c` would return as a real `<b>`
 * element. Ampersand is escaped first, for the same reason as above: otherwise we double-escape
 * the entities this function itself introduces.
 */
export function escapeHtmlText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** One attribute change on an element: a string writes the value, `null` removes the attribute. */
export interface AttributeChange {
  name: string;
  value: string | null;
}

/**
 * Names we are willing to write into an opening tag, per the HTML spec's attribute-name production.
 *
 * A NAME cannot be escaped the way a value can: there is no encoding of `x" onerror="alert(1)` that
 * stays inert once spliced between `<div ` and `="v"`. So an illegal name is refused rather than
 * sanitised.
 */

const ATTRIBUTE_NAME_PATTERN = /^[a-zA-Z_:][a-zA-Z0-9_.:-]*$/;

/**
 * Apply several attribute changes to the element matching `selector`, in ONE resolve and one splice.
 *
 * Batching is not just tidiness. Each resolve re-parses the document and rebuilds the mask, so
 * applying a form's worth of attributes one call at a time cost that work once per attribute — on a
 * 25KB template, eight attributes took 160ms where one takes ~47ms.
 *
 * Values are HTML-escaped; only the specific (selector-resolved) element is touched.
 */
export function setElementAttributes(
  rawHtml: string,
  selector: string,
  changes: AttributeChange[],
): { html: string; success: boolean; error?: string } {
  if (changes.length === 0) return { html: rawHtml, success: true };

  const illegal = changes.find((change) => !ATTRIBUTE_NAME_PATTERN.test(change.name));
  if (illegal) {
    return {
      html: rawHtml,
      success: false,
      error: i18n.t('common:sectionPicker.sectionPickerUtils.invalidAttributeName', {
        attributeName: illegal.name,
      }),
    };
  }

  const resolved = resolveRawElementSpan(rawHtml, selector, { needsSpan: false });
  if (!resolved.ok) {
    return { html: rawHtml, success: false, error: resolveErrorMessage(resolved, selector) };
  }
  const { span } = resolved;

  const parts = splitOpenTag(rawHtml.slice(span.openStart, span.contentStart), span.tagName);
  if (!parts) {
    return {
      html: rawHtml,
      success: false,
      error: i18n.t('common:sectionPicker.sectionPickerUtils.couldNotLocateElement'),
    };
  }

  let attrs = parts.attrs;
  for (const { name, value } of changes) {
    attrs = removeAttrFromSpan(attrs, name);
    if (value !== null) attrs += ` ${name}="${escapeHtmlAttribute(value)}"`;
  }

  const rewritten = `${parts.open}${attrs}${parts.close}`;
  return {
    html: rawHtml.slice(0, span.openStart) + rewritten + rawHtml.slice(span.contentStart),
    success: true,
  };
}

/**
 * Set (or replace) one valued attribute on the element matching `selector`. Replaces the value in
 * place if the attribute is already on that element's opening tag, otherwise appends it.
 */
export function setElementAttribute(
  rawHtml: string,
  selector: string,
  attrName: string,
  attrValue: string,
): { html: string; success: boolean; error?: string } {
  return setElementAttributes(rawHtml, selector, [{ name: attrName, value: attrValue }]);
}

/**
 * Read the value of an attribute on the element matching `selector`. Returns the decoded string
 * value (DOMParser decodes HTML entities for us), or `null` if the element/attribute is absent.
 */
export function getElementAttribute(
  rawHtml: string,
  selector: string,
  attrName: string,
): string | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');
  const root = doc.documentElement;
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- querySelector on Element is valid
  const element = root.querySelector(selector);
  if (!element) return null;
  return element.getAttribute(attrName);
}

export interface ElementSnapshot {
  tagName: string;
  innerHTML: string;
  /** Full text content, untruncated. */
  textContent: string;
  hasChildElements: boolean;
  /** Every attribute in document order, values DECODED (DOMParser resolves entities). */
  attributes: Array<{ name: string; value: string }>;
}

/**
 * Read the element matching `selector` into the same shape a picker click reports, so a consumer
 * can treat "user clicked this element" and "user asked to edit this section" as one input.
 *
 * Attributes are reported raw and unfiltered — deciding which of them a user may edit is policy
 * that belongs to the consumer, not to this parser.
 */
export function getElementSnapshot(rawHtml: string, selector: string): ElementSnapshot | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');
  const root = doc.documentElement;
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- querySelector on Element is valid
  const element = root.querySelector(selector);
  if (!element) return null;

  return {
    tagName: element.tagName.toLowerCase(),
    innerHTML: element.innerHTML,
    textContent: (element.textContent ?? '').trim(),
    hasChildElements: hasMarkupChildren(element),
    attributes: Array.from(element.attributes).map((attr) => ({
      name: attr.name,
      value: attr.value,
    })),
  };
}

/**
 * Remove a valued attribute (e.g. `data-selection-helper="..."`) globally from the raw HTML.
 * Mirrors `removeUpdatableAttribute`'s approach.
 */
export function removeElementAttribute(rawHtml: string, attrName: string): string {
  // eslint-disable-next-line i18next/no-literal-string -- regex flags
  return rawHtml.replace(buildAttrRegex(attrName, 'gi'), '');
}

/**
 * Remove an attribute from the specific element matching `selector` (nth-occurrence aware), leaving
 * the same attribute on other elements untouched — unlike `removeElementAttribute`, which strips it
 * globally. Returns the HTML unchanged if the element or attribute is absent.
 */
export function removeElementAttributeBySelector(
  rawHtml: string,
  selector: string,
  attrName: string,
): string {
  const result = setElementAttributes(rawHtml, selector, [{ name: attrName, value: null }]);
  return result.success ? result.html : rawHtml;
}
